import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Factory, Users, Target, Award, Zap, TrendingUp, Globe, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import WhatsAppButton from '@/components/WhatsAppButton';
import Slider from 'react-slick';

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const endValue = parseFloat(end.replace(/[^0-9.]/g, ''));
    const increment = endValue / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= endValue) {
        setCount(endValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef} className="animate-counter-pop">
      {count}{suffix}
    </span>
  );
};

const About = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Generate particles once to avoid re-rendering issues
  const particles = useMemo(() => [
    { id: 0, left: 10, top: 20, duration: 12, delay: 0.5 },
    { id: 1, left: 85, top: 15, duration: 15, delay: 1.2 },
    { id: 2, left: 25, top: 70, duration: 18, delay: 2.1 },
    { id: 3, left: 70, top: 45, duration: 14, delay: 0.8 },
    { id: 4, left: 45, top: 25, duration: 16, delay: 1.8 },
    { id: 5, left: 90, top: 80, duration: 13, delay: 2.5 },
    { id: 6, left: 15, top: 60, duration: 17, delay: 1.5 },
    { id: 7, left: 60, top: 10, duration: 19, delay: 3.2 },
    { id: 8, left: 35, top: 85, duration: 11, delay: 0.3 },
    { id: 9, left: 80, top: 35, duration: 20, delay: 2.8 },
    { id: 10, left: 5, top: 40, duration: 14, delay: 1.1 },
    { id: 11, left: 95, top: 65, duration: 16, delay: 3.5 },
    { id: 12, left: 50, top: 5, duration: 13, delay: 0.7 },
    { id: 13, left: 75, top: 90, duration: 18, delay: 2.3 },
    { id: 14, left: 20, top: 50, duration: 15, delay: 1.9 }
  ], []);

  useEffect(() => {
    document.title = 'About Us – RSD Accessories Pvt. Ltd. | A Brand of New Eon';
    const timer = setTimeout(() => setIsLoaded(true), 0);
    return () => clearTimeout(timer);
  }, []);

  const values = [
    {
      icon: Factory,
      title: 'Manufacturing Excellence',
      description: 'State-of-the-art manufacturing facility in Noida with advanced quality control systems'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Committed to delivering premium quality products at affordable prices for all customers'
    },
    {
      icon: Target,
      title: 'Innovation Driven',
      description: 'Continuously evolving with latest technology trends and consumer needs'
    },
    {
      icon: Award,
      title: 'Quality Assured',
      description: '1-year warranty on all products with rigorous quality testing and certifications'
    }
  ];

  const stats = [
    { number: '1.6', suffix: 'M+', label: 'Products Sold', icon: TrendingUp },
    { number: '100', suffix: '+', label: 'Distribution Partners', icon: Users },
    { number: '50', suffix: '+', label: 'Cities Covered', icon: Globe },
    { number: '4.7', suffix: '', label: 'Average Rating', icon: Heart }
  ];

  const timeline = [
    { year: '2019', title: 'Company Founded', description: 'RSD Accessories started its journey in Noida as a leading mobile accessories manufacturer' },
    { year: '2020', title: 'Pan India Expansion', description: 'Expanded to 20+ cities, becoming a trusted data cable manufacturer and charger manufacturer' },
    { year: '2022', title: '100+ Partners', description: 'Reached milestone of 100 distribution partners for wholesale mobile accessories' },
    { year: '2024', title: 'Premium Audio Launch', description: 'Introduced TWS earbuds and neckbands as OEM manufacturer' },
    { year: '2025', title: '1.6M+ Products', description: 'Crossed 1.6 million products sold milestone' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Premium Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 animated-gradient opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-transparent" />
        
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-2 h-2 bg-[#FF4D00]/40 rounded-full"
              style={{
                left: `${particle.left}%`,
                top: `${particle.top}%`,
                animation: `particle-float ${particle.duration}s ease-in-out infinite`,
                animationDelay: `${particle.delay}s`
              }}
            />
          ))}
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center ${isLoaded ? 'animate-text-reveal' : 'opacity-0'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-8">
              <Zap className="w-4 h-4 text-[#FF4D00]" />
              <span className="text-white/90 text-sm font-medium">Premium Mobile Accessories Brand</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 text-white">
              About{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-500">RSD</span>
            </h1>
            <h2 
              className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white/90 mb-6 ${isLoaded ? 'animate-text-reveal' : 'opacity-0'}`}
              style={{ animationDelay: '0.3s' }}
            >
              A Brand Of <span className="text-[#FF4D00]">New Eon</span>
            </h2>
            <p 
              className={`text-lg md:text-xl text-white/70 max-w-3xl mx-auto ${isLoaded ? 'animate-text-reveal' : 'opacity-0'}`}
              style={{ animationDelay: '0.5s' }}
            >
              India&apos;s Leading Mobile Accessories Manufacturer | Data Cable Manufacturer | Charger Manufacturer | OEM Manufacturer | Mobile Accessories at Wholesale Price
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C480,150 960,0 1440,64 L1440,120 L0,120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`${isLoaded ? 'animate-slide-left' : 'opacity-0'}`}>
              <span className="text-[#FF4D00] font-semibold text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                India&apos;s Trusted <span className="text-[#FF4D00]">Mobile Accessories Manufacturer</span>
              </h2>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Founded with a vision to make premium quality mobile accessories accessible to every Indian, 
                RSD Accessories has grown to become one of the most trusted data cable manufacturer and charger manufacturer in the industry. We offer mobile accessories at wholesale price.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed">
                Our state-of-the-art manufacturing facility in Noida serves as an OEM manufacturer producing a wide range of products including 
                chargers, cables, earbuds, and neckbands – all designed with Indian consumers in mind.
              </p>
            </div>
            
            <div className={`grid grid-cols-2 gap-4 ${isLoaded ? 'animate-slide-right' : 'opacity-0'}`}>
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className="bg-neutral-50 p-6 rounded-2xl hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 hover:-translate-y-2 group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold text-sm mb-2">{value.title}</h4>
                  <p className="text-xs text-neutral-500">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/about/factory.png"
                alt="RSD Accessories Manufacturing Facility"
                data-testid="factory-image"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
                style={{ maxHeight: '600px' }}
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-neutral-700">
                <p>
                  RSD is more than just a brand — it is a journey driven by vision, quality, and trust.
                </p>
                <p>
                  Established in 2019, RSD began its journey in the trading sector, with a clear focus on understanding market needs and delivering reliable products. With time, experience, and a strong commitment to excellence, we evolved from trading into in-house manufacturing, strengthening our control over quality, innovation, and customer satisfaction.
                </p>
                <p>
                  What started from just two cities has today grown into a strong presence across multiple cities and states throughout India. This growth reflects the confidence our customers and partners place in us and the consistent value we strive to deliver every day.
                </p>
                <p>
                  At RSD, we believe that technology should be accessible, durable, and thoughtfully designed. Our products are developed with a strong emphasis on performance, safety, and modern design, ensuring they meet both national and international quality standards.
                </p>
                <p>
                  Our manufacturing processes combine advanced technology, skilled craftsmanship, and strict quality checks, enabling us to deliver products that stand out in reliability and long-term performance.
                </p>
                <p className="font-semibold text-black">
                  As we continue to grow, our focus remains unchanged — to innovate continuously, expand responsibly, and build a brand that represents trust, excellence, and long-lasting relationships.
                </p>
                <p className="text-xl font-bold text-[#FF4D00] mt-6">
                  RSD — Powering Connectivity. Building Trust.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Stats Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-black via-neutral-900 to-black text-white relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF4D00]/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Impact in Numbers</h2>
            <p className="text-neutral-400">Growing stronger every day</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                data-testid={`stat-${index}`} 
                className="text-center group"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-orange-500/30">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl md:text-5xl font-black text-[#FF4D00] mb-2">
                  <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                </div>
                <div className="text-lg text-neutral-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF4D00] font-semibold text-sm uppercase tracking-wider">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Milestones That Define Us</h2>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#FF4D00] to-orange-300 hidden md:block" />
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div 
                  key={index}
                  className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  style={{ 
                    opacity: 0,
                    animation: isLoaded ? `${index % 2 === 0 ? 'slide-in-left' : 'slide-in-right'} 0.6s ease-out forwards` : 'none',
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <span className="text-[#FF4D00] font-bold text-2xl">{item.year}</span>
                      <h3 className="font-bold text-xl mt-2 mb-2">{item.title}</h3>
                      <p className="text-neutral-600">{item.description}</p>
                    </div>
                  </div>
                  
                  {/* Center dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-[#FF4D00] rounded-full border-4 border-white shadow-lg hidden md:block animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Partners Say</h2>
            <p className="text-lg text-neutral-600">Trusted by distributors and retailers across India for wholesale mobile accessories</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;RSD Accessories has transformed our business. As a leading data cable manufacturer and charger manufacturer, their products are not only reliable but also highly demanded by customers. The after-sales support makes them stand out.&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  N
                </div>
                <div>
                  <p className="font-bold text-lg">NAGPAL TELE</p>
                  <p className="text-sm text-neutral-500">Distributor – Delhi NCR</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;We&rsquo;ve been associated with RSD for over two years. As a mobile accessories manufacturer, their quality and consistency are unmatched. Mobile accessories at wholesale price with premium quality – our customers trust the brand.&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  A
                </div>
                <div>
                  <p className="font-bold text-lg">ABDG</p>
                  <p className="text-sm text-neutral-500">Mumbai</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;The new RSD TWS earbuds and speakers have been a hit among youngsters. As an OEM manufacturer, they deliver premium sound quality at an affordable wholesale price – exactly what the market needs.&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  T
                </div>
                <div>
                  <p className="font-bold text-lg">The Mobile Junction</p>
                  <p className="text-sm text-neutral-500">Assam</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;Working with RSD Accessories feels like a true partnership. As a trusted charger manufacturer and data cable manufacturer, they are committed, innovative, and always deliver on time.&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  A
                </div>
                <div>
                  <p className="font-bold text-lg">Agni Accessories</p>
                  <p className="text-sm text-neutral-500">Mumbai</p>
                </div>
              </div>
            </div>

            {/* New Testimonials with SEO */}
            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;RSD is the best mobile accessories manufacturer we have partnered with. Their data cable manufacturer quality is exceptional and mobile accessories at wholesale price help us maintain great margins. Highly recommend their OEM manufacturer services!&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  T
                </div>
                <div>
                  <p className="font-bold text-lg">Tripura Mobile Accessories</p>
                  <p className="text-sm text-neutral-500">Andhra Pradesh</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;As a retailer, finding a reliable charger manufacturer was crucial. RSD delivers premium mobile accessories at wholesale price consistently. Their OEM manufacturer capabilities are top-notch!&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  A
                </div>
                <div>
                  <p className="font-bold text-lg">A&D Lifestyle</p>
                  <p className="text-sm text-neutral-500">Jharkhand</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;Being in the Northeast, we needed a dependable mobile accessories manufacturer. RSD&rsquo;s data cable manufacturer products and charger manufacturer quality have helped us grow. Mobile accessories at wholesale price with fast delivery!&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  D
                </div>
                <div>
                  <p className="font-bold text-lg">Dasas Enterprises</p>
                  <p className="text-sm text-neutral-500">Assam</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 p-8 rounded-2xl border-l-4 border-[#FF4D00]">
              <p className="text-lg text-neutral-700 mb-6 italic leading-relaxed">
                &ldquo;South India&rsquo;s market demands quality. RSD as an OEM manufacturer delivers exactly that. Best mobile accessories manufacturer for wholesale business. Their charger manufacturer range is unbeatable!&rdquo;
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-[#FF4D00] rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                  C
                </div>
                <div>
                  <p className="font-bold text-lg">CMS Mobile</p>
                  <p className="text-sm text-neutral-500">Kerala</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Values</h2>
            <p className="text-lg text-neutral-600">What drives us forward every day</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  data-testid={`value-${index}`}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF4D00] rounded-full mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                  <p className="text-neutral-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Vision & Mission</h2>
            <p className="text-lg text-neutral-600">Building the future of mobile connectivity</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-neutral-900 to-black text-white p-12 rounded-2xl">
              <h2 className="text-3xl font-bold mb-6 text-[#FF4D00]">Our Vision</h2>
              <p className="text-lg text-neutral-300 leading-relaxed mb-4">
                Our vision is to become a globally trusted brand in mobile accessories and smart technology, recognized for innovation, quality, and value. We aspire to lead the industry by setting new standards in connectivity solutions, making RSD a household name worldwide.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                By embracing technology, fostering creativity, and building strong partnerships, we aim to transform the way people connect, communicate, and experience everyday life.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#FF4D00] to-[#E04400] text-white p-12 rounded-2xl">
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg leading-relaxed mb-4">
                Our mission at RSD Accessories is to deliver high-quality, reliable, and innovative mobile accessories and smart gadgets that enhance everyday connectivity. We are committed to providing products that combine durability, affordability, and the latest technology, ensuring an exceptional experience for our customers.
              </p>
              <p className="text-lg leading-relaxed mb-4">
                With a belief in <strong>&lsquo;Strengthening Every Connection&rsquo;</strong>, RSD stands for excellence, trust, and innovation in every product we create—whether it is data cables, chargers, or speakers.
              </p>
              <p className="text-lg leading-relaxed">
                We strive to grow together with our customers, partners, and team, building a journey of trust, progress, and lasting relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Unique */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">💡 What Makes Us Unique</h2>
            <p className="text-lg text-neutral-600">Setting new standards in mobile accessories</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-[#FF4D00]">
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-3">🏭</span>
                In-House Manufacturing Control
              </h3>
              <p className="text-neutral-600">
                Complete quality and cost efficiency through our own manufacturing facility in Noida
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-[#FF4D00]">
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-3">💎</span>
                Affordable + Premium Positioning
              </h3>
              <p className="text-neutral-600">
                Serving both mass market and aspirational customers with the perfect balance of quality and price
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-[#FF4D00]">
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-3">🤝</span>
                Proven Traction & Trust
              </h3>
              <p className="text-neutral-600">
                Strong distributor network with repeat buyers who trust our brand and quality
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-[#FF4D00]">
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-3">🌍</span>
                Global Vision
              </h3>
              <p className="text-neutral-600">
                Expanding from India to Middle East and beyond with world-class products
              </p>
            </div>
          </div>

          {/* Key Numbers */}
          <div className="bg-black text-white rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#FF4D00]">Key Numbers That Matter</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-black text-[#FF4D00] mb-2">80%</div>
                <p className="text-lg text-neutral-300">Repeat Customers</p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-black text-[#FF4D00] mb-2">55+</div>
                <p className="text-lg text-neutral-300">SKUs Available</p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-black text-[#FF4D00] mb-2">6+</div>
                <p className="text-lg text-neutral-300">Years Manufacturing Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Showcase */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Manufacturing Facility</h2>
            <p className="text-lg text-neutral-600">Modern manufacturing and world-class infrastructure</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="/images/about/office.png"
                alt="RSD Accessories Office Building"
                className="w-full h-64 object-cover"
              />
              <div className="p-4 bg-white">
                <h3 className="font-bold text-lg mb-1">Corporate Office</h3>
                <p className="text-neutral-600 text-sm">B-08, Sector-57, Noida</p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="/images/about/factory.png"
                alt="Manufacturing Floor"
                className="w-full h-64 object-cover"
              />
              <div className="p-4 bg-white">
                <h3 className="font-bold text-lg mb-1">Production Floor</h3>
                <p className="text-neutral-600 text-sm">Cable manufacturing unit</p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="/images/about/reception.png"
                alt="Reception Area"
                className="w-full h-64 object-cover"
              />
              <div className="p-4 bg-white">
                <h3 className="font-bold text-lg mb-1">Reception Area</h3>
                <p className="text-neutral-600 text-sm">Professional workspace</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Made in India */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-green-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-6">🇮🇳</div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Proudly <span className="text-[#FF4D00]">Made in India</span>
          </h2>
          <p className="text-xl text-neutral-700 mb-8">
            We are committed to the &ldquo;Make in India&rdquo; initiative, supporting local employment and contributing to the nation&rsquo;s economic growth. Our manufacturing facility in Noida stands as a testament to Indian engineering excellence.
          </p>
          <div className="inline-flex items-center px-6 py-3 bg-white rounded-full shadow-lg">
            <span className="text-lg font-bold accent-font">MANUFACTURED IN NOIDA, INDIA</span>
          </div>
        </div>
      </section>

      {/* Product Showcase Room */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF4D00] font-semibold text-sm uppercase tracking-wider">Product Display</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Our Product Showcase</h2>
            <p className="text-lg text-neutral-600">Premium accessories displayed at our showroom</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/about/oem-factory.webp"
                alt="RSD Accessories Product Showcase Room"
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">RSD Product Display Center</h3>
                <p className="text-white/80">Wide range of chargers, cables, earbuds, and accessories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Authorized Distributors Gallery - Slider */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-neutral-900 via-black to-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF4D00] font-semibold text-sm uppercase tracking-wider">Our Network</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Authorized Distributors</h2>
            <p className="text-lg text-neutral-400">Proud partners who trust RSD Accessories across India</p>
          </div>
          
          {/* Distributor Slider */}
          <div className="relative px-8">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={4}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              pauseOnHover={true}
              prevArrow={
                <button className="slick-prev absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#FF4D00] hover:bg-[#E04400] p-2 rounded-full transition-all">
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
              }
              nextArrow={
                <button className="slick-next absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[#FF4D00] hover:bg-[#E04400] p-2 rounded-full transition-all">
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              }
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                  }
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                  }
                },
                {
                  breakpoint: 480,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  }
                }
              ]}
            >
              {/* Distributor 1 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert1.jpg"
                      alt="Indo Electronics - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 2 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert2.jpg"
                      alt="Mobile Gallery - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 3 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert3.jpg"
                      alt="RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 4 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert4.jpg"
                      alt="RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 5 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert5.jpg"
                      alt="RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 6 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert6.jpg"
                      alt="RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 7 - Gulati Mobile Hut */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert7.jpg"
                      alt="Gulati Mobile Hut - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 8 - Sarna Communications */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert8.jpg"
                      alt="Sarna Communications - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 9 - Friends Telecom */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert9.jpg"
                      alt="Friends Telecom - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distributor 10 - Gulati Mobile Hut 2 */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert10.jpg"
                      alt="Gulati Mobile Hut - RSD Authorized Distributor"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-medium">Authorized Partner</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Certificate */}
              <div className="px-2">
                <div className="group relative rounded-xl overflow-hidden bg-neutral-800 hover:bg-neutral-700 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src="/images/about/cert-main.jpg"
                      alt="RSD Authorization Certificate"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#FF4D00] rounded-full animate-pulse" />
                      <span className="text-xs text-[#FF4D00] font-medium">Official Certificate</span>
                    </div>
                  </div>
                </div>
              </div>
            </Slider>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-neutral-400 mb-4">Want to become an authorized RSD distributor?</p>
            <a 
              href="/become-distributor"
              className="inline-flex items-center gap-2 bg-[#FF4D00] hover:bg-[#E04400] text-white px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105"
            >
              Become a Partner
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default About;